












































































import { Component, Vue } from 'vue-property-decorator';
import { HttpError } from '@easygenerator/fragment-sdk';
import Notification from '@/components/notification/Notification.vue';
import InputField from '@/components/auth-form/InputField.vue';
import SocialAuth from '@/components/auth-form/SocialAuth.vue';
import PasswordField from '@/components/auth-form/PasswordField.vue';
import Checkbox from '@/components/auth-form/Checkbox.vue';

import { EULA_URL } from '../../constants';
import { AuthenticationErrorCode, IAuthForbiddenResult } from '../../types/auth';
import { OnValidatedCallback } from '../../types/constants';
import { HttpStatus } from '../../types/http';

const SUCCESSFUL_NOTIFICATION_TIMEOUT = 4000;
const ERROR_NOTIFICATION_TIMEOUT = 5000;

@Component({
  components: { Checkbox, PasswordField, Notification, InputField, SocialAuth },
  metaInfo(this: LoginForm) {
    return {
      meta: this.metaTags,
      title: this.$t('learnerPageTitle')
    };
  }
})
export default class LoginForm extends Vue {
  get eulaUrl() {
    return EULA_URL;
  }

  get metaTags() {
    return [
      { name: 'title', content: this.$t('learnerPageTitle') },
      { name: 'description', content: this.$t('signInDescription') },
      { property: 'og:title', content: this.$t('learnerPageTitle') },
      { property: 'og:description', content: this.$t('signInDescription') }
    ];
  }

  emailValue: string = '';

  passwordValue: string = '';

  rememberMe: boolean = false;

  notifications = {
    successful: {
      show: false,
      text: '',
      timeoutId: 0
    },
    error: {
      show: false,
      text: '',
      timeoutId: 0
    }
  };

  emailPattern = (): RegExp => /.+@.+/g;

  async validateForm() {
    return (await Promise.all([this.validateField('email'), this.validateField('password')])).every(item => item);
  }

  async validateField(name: string) {
    return new Promise(resolve => {
      const callback: OnValidatedCallback = async isValidPromise => resolve(await isValidPromise);

      this.$emit(`validate:${name}`, callback);
    });
  }

  rememberMeUpdated() {
    this.rememberMe = !this.rememberMe;
  }

  async submitForm() {
    const isFormValid = await this.validateForm();
    if (!isFormValid) {
      return;
    }

    const email = this.emailValue.trim();
    const password = this.passwordValue.trim();

    try {
      await this.$store.dispatch('app/login', { email, password, shortTermAccess: !this.rememberMe });

      this.$router.push({ name: 'Dashboard' });
    } catch (e) {
      const error = e as HttpError;
      if (error.status === HttpStatus.Forbidden) {
        this.handleForbiddenResult(error.body as IAuthForbiddenResult<AuthenticationErrorCode>);
        return;
      }

      this.showErrorNotification(this.$t('errorLoginEmail'));
    }
  }

  handleForbiddenResult(body: IAuthForbiddenResult<AuthenticationErrorCode>) {
    switch (body.errorCode) {
      case AuthenticationErrorCode.accountIsBeingDeleted: {
        this.showErrorNotification(this.$t('accountDeletionInProgress'));
        break;
      }
      case AuthenticationErrorCode.accountDeactivated: {
        this.showErrorNotification(this.$t('accountDeactivated'));
        break;
      }
      case AuthenticationErrorCode.tooManyLoginAttempts: {
        this.showErrorNotification(this.$t('tooManyLoginAttempts'));
        break;
      }
      case AuthenticationErrorCode.accountBlocked: {
        this.showErrorNotification(this.$t('accountBlocked'));
        break;
      }
      default: {
        this.showErrorNotification(this.$t('commonSignInErrorMessage'));
        break;
      }
    }
  }

  async forgotPassword() {
    if (!(await this.validateField('email'))) {
      return;
    }

    const email = this.emailValue.trim();
    await this.$store.dispatch('app/forgotPassword', { email });

    this.showSuccessfulNotification(this.$t('resetPasswordLinkSentSuccess'));
  }

  showSuccessfulNotification(text: string) {
    clearTimeout(this.notifications.successful.timeoutId);

    this.notifications.successful.text = text;
    this.notifications.successful.show = true;

    this.notifications.successful.timeoutId = setTimeout(() => {
      this.notifications.successful.show = false;
    }, SUCCESSFUL_NOTIFICATION_TIMEOUT) as unknown as number;
  }

  showErrorNotification(text: string) {
    clearTimeout(this.notifications.error.timeoutId);

    this.notifications.error.text = text;
    this.notifications.error.show = true;

    this.notifications.error.timeoutId = setTimeout(() => {
      this.notifications.error.show = false;
    }, ERROR_NOTIFICATION_TIMEOUT) as unknown as number;
  }
}
